/**
 * UsageSection - Settings panel for the token/cost report.
 *
 * Reads `GET /api/admin/usage/stats` via `adminFetch`. Gated on the
 * `usage.view` global permission — read-only and installation-wide, there is
 * no project-scoped variant (see the permission's doc comment in
 * `packages/contracts/src/auth.ts`).
 *
 * Rendering rules the server's response shape forces on us (see
 * `UsageStatsTotals` in `packages/contracts/src/usage.ts`):
 *  - `cachedInputTokens` and `cacheCreationInputTokens` are SUBSETS of
 *    `inputTokens`; `reasoningOutputTokens` is a subset of `outputTokens`.
 *    Never add them to their parent column. `totalTokens` is the only whole
 *    figure, and it already equals `inputTokens + outputTokens`.
 *  - `costUsd: null` means unknown, not zero. A group can mix Claude (priced)
 *    and Codex (unpriced) rows, in which case the sum is a floor — rendered
 *    as "≥ $x" with a note.
 *  - `key: null` is a real bucket ("Unassigned"), not something to filter.
 *  - `rows: []` with all-zero totals is a legitimate empty state, not an
 *    error — fresh installs have no backfilled usage.
 */
import type {
  ProviderKind,
  TurnUsageKind,
  UsageStatsGroupBy,
  UsageStatsResult,
  UserId,
} from "@taskin-code/contracts";
import {
  PROVIDER_DISPLAY_NAMES,
  TURN_USAGE_KIND_VALUES,
  USAGE_STATS_GROUP_BY_VALUES,
} from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useHasGlobalPermission } from "../../auth/authStore";
import { adminFetch } from "../../lib/adminFetch";
import { RefreshCwIcon } from "../../lib/icons";
import { useUserDirectoryMap } from "../../lib/userDirectoryReactQuery";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const GROUP_BY_LABELS: Record<UsageStatsGroupBy, string> = {
  model: "Model",
  user: "User",
  thread: "Thread",
  agent: "Agent",
  day: "Day",
  kind: "Operation",
};

/**
 * Readable names for the `kind` axis, which is the only one whose values are a
 * closed set we own. The raw slugs are readable enough to survive a missing entry
 * — hence the fallback in `resolveKeyLabel` rather than a `Record` that must be
 * exhaustive — but "Commit message" beats `commit-message` in a report someone
 * shows a stakeholder.
 */
const KIND_LABELS: Partial<Record<TurnUsageKind, string>> = {
  chat: "Chat turns",
  "thread-title": "Thread titles",
  "commit-message": "Commit messages",
  "pr-content": "PR title & body",
  "branch-name": "Branch names",
  "thread-context": "Thread context",
  "thread-recap": "Recap del hilo",
};

/**
 * Counter columns: right-aligned and tabular so digits line up down the column,
 * and non-wrapping so a wide viewport spends its slack on the key column
 * instead of on the numbers.
 */
const NUMERIC_HEAD_CLASS = "whitespace-nowrap px-4 py-2 text-right font-medium";
const NUMERIC_CELL_CLASS =
  "whitespace-nowrap px-4 py-3 text-right tabular-nums text-muted-foreground";
const NUMERIC_FOOT_CLASS = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

const numberFormatter = new Intl.NumberFormat("en-US");
const formatCount = (value: number): string => numberFormatter.format(value);

/** `null` cost is unknown, never rendered as zero. */
const formatCost = (costUsd: number | null, hasMixedProviders: boolean): string => {
  if (costUsd === null) return "—";
  const formatted = `$${costUsd.toFixed(2)}`;
  return hasMixedProviders ? `≥ ${formatted}` : formatted;
};

interface FiltersState {
  groupBy: UsageStatsGroupBy;
  from: string;
  to: string;
  threadId: string;
  userId: string;
  model: string;
  provider: "" | ProviderKind;
  /** Empty string means "every operation", the same convention as `provider`. */
  kind: "" | TurnUsageKind;
}

const DEFAULT_FILTERS: FiltersState = {
  groupBy: "model",
  from: "",
  to: "",
  threadId: "",
  userId: "",
  model: "",
  provider: "",
  kind: "",
};

function buildQueryString(filters: FiltersState): string {
  const params = new URLSearchParams();
  params.set("groupBy", filters.groupBy);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.threadId.trim()) params.set("threadId", filters.threadId.trim());
  if (filters.userId.trim()) params.set("userId", filters.userId.trim());
  if (filters.model.trim()) params.set("model", filters.model.trim());
  if (filters.provider) params.set("provider", filters.provider);
  if (filters.kind) params.set("kind", filters.kind);
  return params.toString();
}

export function UsageSection() {
  const canView = useHasGlobalPermission("usage.view");
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  /**
   * The filters the table is actually showing, which only advance when the
   * refresh button is pressed. Editing a filter must not fire a query: these
   * are aggregates over the whole ledger, and typing a thread id would
   * otherwise bill the database for one full scan per keystroke.
   */
  const [appliedFilters, setAppliedFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const userDirectory = useUserDirectoryMap({ enabled: canView });

  const appliedQueryString = buildQueryString(appliedFilters);
  const query = useQuery({
    queryKey: ["usageStats", appliedQueryString],
    queryFn: () => adminFetch<UsageStatsResult>(`/api/admin/usage/stats?${appliedQueryString}`),
    enabled: canView,
  });

  const hasPendingFilters = buildQueryString(filters) !== appliedQueryString;

  /**
   * Applying an unchanged filter set is a plain reload — `setState` with an
   * equal value would not re-run the query, so ask for a refetch instead.
   */
  const runQuery = () => {
    if (hasPendingFilters) {
      setAppliedFilters(filters);
      return;
    }
    void query.refetch();
  };

  // Keyed off the APPLIED filters, not the form: while an edited-but-unapplied
  // groupBy sits in the inputs, the table still shows the previous axis and
  // must keep labelling it that way.
  const resolveKeyLabel = useMemo(() => {
    return (key: string | null): string => {
      if (key === null) return "Unassigned";
      if (appliedFilters.groupBy === "user") {
        return userDirectory.get(key as UserId)?.displayName ?? key;
      }
      if (appliedFilters.groupBy === "kind") {
        return KIND_LABELS[key as TurnUsageKind] ?? key;
      }
      return key;
    };
  }, [appliedFilters.groupBy, userDirectory]);

  if (!canView) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        You need the <code>usage.view</code> permission to see token usage.
      </div>
    );
  }

  const result = query.data;
  // The response never flags whether a non-null costUsd is a complete sum or
  // a floor over a provider mix — Codex reports no cost at all. `groupBy:
  // "model"` can't mix providers (a model slug belongs to one), so the only
  // axes where a row can silently combine priced Claude turns with unpriced
  // Codex turns are user/thread/agent/day, and only when the provider filter
  // isn't already pinning it to one.
  const mayMixProviders = appliedFilters.groupBy !== "model" && appliedFilters.provider === "";

  return (
    // No heading of its own: the settings shell already renders the section's
    // label and description above every panel.
    <div className="flex flex-col gap-4">
      {/* Enter is handled here rather than through a form's implicit submit,
          which never fires: the app binds Enter globally. */}
      <div
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            runQuery();
          }
        }}
        className="flex flex-wrap items-end gap-3 rounded-lg border border-border/60 bg-card/40 p-3"
      >
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Group by
          <select
            value={filters.groupBy}
            onChange={(e) =>
              setFilters((f) => ({ ...f, groupBy: e.target.value as UsageStatsGroupBy }))
            }
            className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
          >
            {USAGE_STATS_GROUP_BY_VALUES.map((value) => (
              <option key={value} value={value}>
                {GROUP_BY_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          From
          <Input
            type="date"
            size="sm"
            value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            className="w-36"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          To
          <Input
            type="date"
            size="sm"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            className="w-36"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Provider
          <select
            value={filters.provider}
            onChange={(e) =>
              setFilters((f) => ({ ...f, provider: e.target.value as "" | ProviderKind }))
            }
            className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
          >
            <option value="">All</option>
            <option value="codex">{PROVIDER_DISPLAY_NAMES.codex}</option>
            <option value="claudeAgent">{PROVIDER_DISPLAY_NAMES.claudeAgent}</option>
          </select>
        </label>

        {/*
          A select and not a free-text input, unlike Model and the ids: the set of
          operations is closed and known, so offering anything else would only let
          someone filter for a kind that cannot exist.
        */}
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Operation
          <select
            value={filters.kind}
            onChange={(e) =>
              setFilters((f) => ({ ...f, kind: e.target.value as "" | TurnUsageKind }))
            }
            className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
          >
            <option value="">All</option>
            {TURN_USAGE_KIND_VALUES.map((value) => (
              <option key={value} value={value}>
                {KIND_LABELS[value] ?? value}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Model
          <Input
            size="sm"
            value={filters.model}
            onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))}
            placeholder="model slug"
            className="w-40"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          User ID
          <Input
            size="sm"
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
            placeholder="user id"
            className="w-40"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Thread ID
          <Input
            size="sm"
            value={filters.threadId}
            onChange={(e) => setFilters((f) => ({ ...f, threadId: e.target.value }))}
            placeholder="thread id"
            className="w-40"
          />
        </label>

        <Button size="sm" variant="outline" disabled={query.isFetching} onClick={runQuery}>
          <RefreshCwIcon className={query.isFetching ? "animate-spin" : undefined} />
          Refresh
        </Button>

        {hasPendingFilters ? (
          <span className="pb-1.5 text-xs text-muted-foreground">
            Filters changed — press Refresh to apply.
          </span>
        ) : null}
      </div>

      {query.isError ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {query.error instanceof Error ? query.error.message : "Failed to load usage"}
        </div>
      ) : null}

      {/* The panel gets the full viewport width (see `SettingsNavItem.width`), so
          the scroller only kicks in on genuinely narrow screens. */}
      <div className="overflow-x-auto rounded-lg border border-border/60 bg-card/40">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="border-b border-border/40 bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {/* The key column takes the slack so the numbers stay grouped on
                  the right, where they can be compared down a column. */}
              <th className="w-full px-4 py-2 font-medium">
                {GROUP_BY_LABELS[appliedFilters.groupBy]}
              </th>
              <th className={NUMERIC_HEAD_CLASS}>Input</th>
              {/* Reads and writes apart: a fresh session writes its prefix to the
                  cache before it can read any back, and showing reads alone made
                  that look like no caching at all. */}
              <th className={NUMERIC_HEAD_CLASS}>Cache read</th>
              <th className={NUMERIC_HEAD_CLASS}>Cache write</th>
              <th className={NUMERIC_HEAD_CLASS}>Output</th>
              <th className={NUMERIC_HEAD_CLASS}>Total</th>
              <th className={NUMERIC_HEAD_CLASS}>Cost</th>
              <th className={NUMERIC_HEAD_CLASS}>Turns</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading usage…
                </td>
              </tr>
            ) : !result || result.rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No data for the selected range.
                </td>
              </tr>
            ) : (
              result.rows.map((row) => (
                <tr
                  key={row.key ?? "__null__"}
                  className="border-b border-border/30 align-top last:border-b-0"
                >
                  <td className="px-4 py-3 font-medium">{resolveKeyLabel(row.key)}</td>
                  <td className={NUMERIC_CELL_CLASS}>{formatCount(row.totals.inputTokens)}</td>
                  <td className={NUMERIC_CELL_CLASS}>
                    {formatCount(row.totals.cachedInputTokens)}
                  </td>
                  <td className={NUMERIC_CELL_CLASS}>
                    {formatCount(row.totals.cacheCreationInputTokens)}
                  </td>
                  <td className={NUMERIC_CELL_CLASS}>{formatCount(row.totals.outputTokens)}</td>
                  <td className={`${NUMERIC_CELL_CLASS} font-medium text-foreground`}>
                    {formatCount(row.totals.totalTokens)}
                  </td>
                  <td className={NUMERIC_CELL_CLASS}>
                    {formatCost(row.totals.costUsd, mayMixProviders)}
                  </td>
                  <td className={NUMERIC_CELL_CLASS}>{formatCount(row.totals.turnCount)}</td>
                </tr>
              ))
            )}
          </tbody>
          {result && result.rows.length > 0 ? (
            <tfoot className="border-t border-border/40 bg-muted/20 font-medium">
              <tr>
                <td className="px-4 py-2">Total</td>
                <td className={NUMERIC_FOOT_CLASS}>{formatCount(result.totals.inputTokens)}</td>
                <td className={NUMERIC_FOOT_CLASS}>
                  {formatCount(result.totals.cachedInputTokens)}
                </td>
                <td className={NUMERIC_FOOT_CLASS}>
                  {formatCount(result.totals.cacheCreationInputTokens)}
                </td>
                <td className={NUMERIC_FOOT_CLASS}>{formatCount(result.totals.outputTokens)}</td>
                <td className={NUMERIC_FOOT_CLASS}>{formatCount(result.totals.totalTokens)}</td>
                <td className={NUMERIC_FOOT_CLASS}>
                  {formatCost(result.totals.costUsd, mayMixProviders)}
                </td>
                <td className={NUMERIC_FOOT_CLASS}>{formatCount(result.totals.turnCount)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
